import React, { useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Import toastify CSS
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../../Axiosinstance';
import { TextField, FormControl, FormLabel, IconButton, RadioGroup, Radio, Checkbox, CircularProgress, FormControlLabel, Button, Grid, MenuItem, Typography, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { CheckCircleOutline, CloudUpload } from '@mui/icons-material';
import { CLOUDINARY_UPLOAD_PRESET, baseURL } from '../../../common';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

const SelfSponsoredForm = ({ fee, dataFrom }) => {
  const { batchid } = useParams(); // Extract batchId from the URL params
  const [openModal, setOpenModal] = useState(false);
  const [registerId, setRegisterId] = useState('');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    sector: '',
    nationality: '',
    name: '',
    idType: '',
    nric: '',
    dob: '',
    emailId: [''],
    contactNumber: [''],
    cardType: '',
    fee: fee,
    promotionCode: '',
    registration_Form: '',
    ic: '',
    supporting_Documents: '',
    funding: ''
  });


  const [nricValid, setNricValid] = useState(true);

  // Handle NRIC validation onBlur (when user clicks outside)
  const handleNricBlur = async () => {
    try {
      const response = await axios.get(`/wsq/companies/check-nric?nric=${formData.nric}`);

      if (response.status === 200) {
        setNricValid(true);
      }
    } catch (error) {
      if (error.response && error.response.status === 409) {
        setNricValid(false);
        toast.error('Duplicate NRIC found. Please check again.', {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error('Error validating NRIC. Please try again later.', {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    }
  };

  const handleArrayChange = (field, index, value) => {
    const updatedArray = [...formData[field]];
    updatedArray[index] = value;
    setFormData({ ...formData, [field]: updatedArray });
  };

  const addArrayItem = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const removeArrayItem = (field, index) => {
    const updatedArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: updatedArray });
  };


  // Handle form field changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle file uploads
  const handleFileChange = (e, fieldName) => {
    handleFileUpload(e, 0, 0, fieldName); // Assuming single student for now
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      batchId: batchid,
      students: { ...formData },
    };

    setLoading(true);

    try {
      const response = await axios.post('/wsq/companies/add/self-students', payload);
      if (response.status === 200) {
        setRegisterId(response.data.registerId);
        setOpenModal(true); // Open success modal
      }
    } catch (error) {
      toast.error('Error submitting the form. Please try again.', {
        position: toast.POSITION.TOP_RIGHT,
      });
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false); // Stop the loading spinner
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    // navigate('/HomeContainer'); // Navigate to HomeContainer after modal close
    if (dataFrom === 'homecontainerpage') {
      navigate('/HomeContainer');
    } else {
      navigate('/wsq/student'); // Replace '/AnotherPath' with the desired route
    }

  };
  const handleFileUpload = async (e, contactIndex, studentIndex, fieldName) => {
    try {
      const file = e.target.files[0];
      if (!file) return;

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

      const response = await fetch(baseURL, {
        method: 'POST',
        body: formDataUpload,
      });

      const data = await response.json();

      if (data.secure_url) {
        const uploadedFileUrl = data.secure_url;

        setFormData((prevState) => ({
          ...prevState,
          [fieldName]: uploadedFileUrl,
        }));
      }
    } catch (err) {
      console.error('File upload error:', err);
    }
  };

  const todayDate = new Date().toISOString().split('T')[0];

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <TextField
          label="Sector"
          name="sector"
          variant="outlined"
          fullWidth
          margin="normal"
          required
          value={formData.sector}
          onChange={handleChange}
        />
        <TextField
          label="Nationality"
          name="nationality"
          variant="outlined"
          fullWidth
          margin="normal"
          required
          value={formData.nationality}
          onChange={handleChange}
        />

        <TextField
          label="Full Name (As per NRIC)"
          name="name"
          variant="outlined"
          fullWidth
          margin="normal"
          required
          value={formData.name}
          onChange={handleChange}
        />
        <TextField
          label="ID Type"
          name="idType"
          select
          variant="outlined"
          fullWidth
          margin="normal"
          required
          value={formData.idType}
          onChange={handleChange}
        >
          <MenuItem value="NRIC">NRIC</MenuItem>
          <MenuItem value="FIN">FIN</MenuItem>
          <MenuItem value="Passport">Passport</MenuItem>
        </TextField>
        <TextField
          label={formData.idType || "ID"}
          name={"nric"}
          variant="outlined"
          fullWidth
          margin="normal"
          required
          value={formData.nric}
          onChange={handleChange}
          onBlur={handleNricBlur} // Trigger NRIC check when user clicks outside
          error={!nricValid} // Show error if NRIC is invalid
          helperText={!nricValid && "NRIC number already existing."} // Helper text for invalid NRIC
        />
        <TextField
          label="Date of Birth"
          name="dob"
          type="date"
          variant="outlined"
          fullWidth
          margin="normal"
          required
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            max: todayDate, // Restrict selection to today or earlier
          }}
          value={formData.dob}
          onChange={handleChange}
        />
        <div>
          <Typography variant="h6" gutterBottom>
            Email Addresses
          </Typography>
          {formData.emailId.map((email, index) => (
            <Grid container alignItems="center" spacing={1} key={`email-${index}`} style={{ marginBottom: '10px' }}>
              {/* Email Input */}
              <Grid item xs={10}>
                <TextField
                  label="Email"
                  type="email"
                  variant="outlined"
                  fullWidth
                  required
                  value={email}
                  onChange={(e) => handleArrayChange('emailId', index, e.target.value)}
                />
              </Grid>

              {/* Add Button */}
              <Grid>
                <IconButton color="primary" onClick={() => addArrayItem('emailId')}>
                  <AddCircleOutlineIcon />
                </IconButton>
              </Grid>

              {/* Remove Button */}
              <Grid>
                {index > 0 && (
                  <IconButton color="error" onClick={() => removeArrayItem('emailId', index)}>
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}

          <Typography variant="h6" gutterBottom>
            Contact Numbers
          </Typography>
          {formData.contactNumber.map((contactNumber, index) => (
            <Grid container alignItems="center" spacing={1} key={`contact-${index}`} style={{ marginBottom: '10px' }}>
              {/* Phone Input */}
              <Grid item xs={10}>
                <PhoneInput
                  country={'sg'} // Default country code
                  value={contactNumber}
                  onChange={(value, country) =>
                    handleArrayChange(
                      'contactNumber',
                      index,
                      `+${country.dialCode} ${value.slice(country.dialCode.length)}`
                    )
                  }
                  inputStyle={{
                    width: '100%',
                    height: '56px', // Match Material-UI TextField height
                    borderRadius: '4px',
                  }}
                />
              </Grid>

              {/* Add Button */}
              <Grid>
                <IconButton color="primary" onClick={() => addArrayItem('contactNumber')}>
                  <AddCircleOutlineIcon />
                </IconButton>
              </Grid>

              {/* Remove Button */}
              <Grid>
                {index > 0 && (
                  <IconButton color="error" onClick={() => removeArrayItem('contactNumber', index)}>
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}
        </div>

        <TextField
          label="Card Type"
          name="cardType"
          variant="outlined"
          fullWidth
          margin="normal"
          required
          value={formData.cardType}
          onChange={handleChange}
        />

        <FormControl component="fieldset" margin="normal" fullWidth>
          <FormLabel component="legend">Funding</FormLabel>
          <RadioGroup
            row
            name="funding"
            value={formData.funding || 'no'} // Default to 'no' if no selection is made
            onChange={(event) => setFormData({ ...formData, funding: event.target.value || 'no' })}
          >
            <FormControlLabel
              value="yes"
              control={<Radio />}
              label="Yes"
            />
            <FormControlLabel
              value="no"
              control={<Radio />}
              label="No"
            />
          </RadioGroup>
        </FormControl>



        {/* File upload fields */}
        <Typography variant="h6" gutterBottom>Upload Documents:</Typography>
        <Grid container spacing={2}>
          {/* Registration Form Upload */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              component="label"
              fullWidth
              startIcon={<CloudUpload />} // Add an icon to the button
              sx={{ backgroundColor: '#1976d2', color: '#fff', marginBottom: '10px' }}
            >
              Upload Registration Form
              <input
                type="file"
                hidden // Hide the default file input
                accept=".pdf,.jpg,.png"
                onChange={(e) => handleFileChange(e, 'registration_Form')}

              />
            </Button>
            {formData.registration_Form && (
              <Typography variant="body2" color="textSecondary">
                Selected file: {formData.registration_Form.split('/').pop()}
              </Typography>
            )}
          </Grid>

          {/* IC Upload */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              component="label"
              fullWidth
              startIcon={<CloudUpload />}
              sx={{ backgroundColor: '#1976d2', color: '#fff', marginBottom: '10px' }}
            >
              Upload IC
              <input
                type="file"
                hidden
                accept=".pdf,.jpg,.png"
                onChange={(e) => handleFileChange(e, 'ic')}

              />
            </Button>
            {formData.ic && (
              <Typography variant="body2" color="textSecondary">
                Selected file: {formData.ic.split('/').pop()}
              </Typography>
            )}
          </Grid>

          {/* Supporting Documents Upload */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              component="label"
              fullWidth
              startIcon={<CloudUpload />}
              sx={{ backgroundColor: '#1976d2', color: '#fff', marginBottom: '10px' }}
            >
              Upload Supporting Documents
              <input
                type="file"
                hidden
                accept=".pdf,.jpg,.png"
                onChange={(e) => handleFileChange(e, 'supporting_Documents')}

              />
            </Button>
            {formData.supporting_Documents && (
              <Typography variant="body2" color="textSecondary">
                Selected file: {formData.supporting_Documents.split('/').pop()}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Typography variant="h6" gutterBottom>
          Course Amount: SGD ${fee} (EXCL. GST)
        </Typography>
        <TextField
          label="Promotion Code"
          name="promotionCode"
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.promotionCode}
          onChange={handleChange}
        />


        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
          <Button
            type="button"
            variant="contained"
            color="error"
            onClick={() => {
              if (dataFrom === 'homecontainerpage') {
                navigate('/HomeContainer');
              } else {
                navigate('/wsq/student'); // Replace '/AnotherPath' with the desired route
              }
            }}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </div>



        <Dialog open={openModal} onClose={handleCloseModal}>
          <DialogTitle style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '24px', color: '#4caf50' }}>
            Registration Successful
          </DialogTitle>
          <DialogContent>
            <Grid container justifyContent="center">

              <CheckCircleOutline style={{ fontSize: '80px', color: '#4caf50' }} />
            </Grid>
            <DialogContentText
              style={{
                textAlign: 'center',
                fontSize: '18px',
                marginTop: '20px',
                color: '#555',
              }}
            >
              Your registration was successfully submitted!
            </DialogContentText>
            <DialogContentText
              style={{
                textAlign: 'center',
                fontSize: '16px',
                marginTop: '10px',
                color: '#777',
              }}
            >
              Your registration ID is: <strong>{registerId}</strong><br />
              We have received your information and will be in touch with further details shortly.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Grid container justifyContent="center">
              <Button
                onClick={handleCloseModal}
                variant="contained"
                style={{
                  backgroundColor: '#4caf50',
                  color: '#fff',
                  padding: '10px 30px',
                  fontSize: '16px',
                }}
              >
                OK
              </Button>
            </Grid>
          </DialogActions>
        </Dialog>
      </form>
      <ToastContainer />
    </div>
  );
};

export default SelfSponsoredForm;

